import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import WebApp from '@twa-dev/sdk';
import TelegramWebApp from '../../telegram-init';
import { SectionWrapper } from '../SectionWrapper';
import { GradientTitle } from '../GradientTitle';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';

export const SupportSection: React.FC = () => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const { isIntersecting } = useIntersectionObserver({
    ref: containerRef,
    threshold: 0.1
  });

  const handleEmailClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    try {
      const button = e.currentTarget;
      const rect = button.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      const ripple = document.createElement('span');
      ripple.className = 'absolute rounded-full bg-white/30 pointer-events-none';
      ripple.style.left = x + 'px';
      ripple.style.top = y + 'px';
      ripple.style.animation = 'ripple-effect 0.6s linear';
      
      button.appendChild(ripple);
      
      setTimeout(() => {
        ripple.remove();
      }, 600);
      
      const email = 'bigdeal.helpdesk@gmail.com';
      const tempInput = document.createElement('input');
      tempInput.value = email;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand('copy');
      document.body.removeChild(tempInput);

      TelegramWebApp.HapticFeedback.impactOccurred('medium');
      
      TelegramWebApp.showPopup({
        message: t('PROFILE_SCREEN.FOOTER.EMAIL_COPIED') || 'Email copied to clipboard',
        buttons: [{ type: 'default', text: 'OK' }]
      });
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleTelegramClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    try {
      const button = e.currentTarget;
      const rect = button.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      const ripple = document.createElement('span');
      ripple.className = 'absolute rounded-full bg-white/30 pointer-events-none';
      ripple.style.left = x + 'px';
      ripple.style.top = y + 'px';
      ripple.style.animation = 'ripple-effect 0.6s linear';
      
      button.appendChild(ripple);
      
      setTimeout(() => {
        ripple.remove();
      }, 600);
      
      WebApp.openTelegramLink('https://t.me/BigDealSupportBot');
      TelegramWebApp.HapticFeedback.impactOccurred('medium');
    } catch (error) {
      console.error('Failed to open Telegram link:', error);
    }
  };

  return (
    <SectionWrapper>
      <GradientTitle
        title={t('PROFILE_SCREEN.FOOTER.SUPPORT_TITLE') || 'Support'}
        caption={t('PROFILE_SCREEN.FOOTER.SUPPORT_CAPTION') || 'Contact us if you have any questions'}
      />
      
      <div ref={containerRef} className="flex flex-col gap-3">
        <div className="flex items-center gap-4 p-4 bg-tg-secondary-bg rounded-xl border border-pink-500/10 transition-all duration-300 relative overflow-hidden hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(0,0,0,0.12)] hover:border-pink-500/20 active:-translate-y-0.5 active:shadow-[0_4px_12px_rgba(0,0,0,0.1)] max-[360px]:p-3 max-[360px]:gap-2.5">
          <div 
            className="text-[32px] flex items-center justify-center w-16 h-16 bg-gradient-to-br from-pink-500/8 to-orange-500/8 rounded-full flex-shrink-0 border border-pink-500/15 shadow-[0_4px_12px_rgba(0,0,0,0.06),inset_0_2px_4px_rgba(255,255,255,0.1)] relative z-[2] transition-all duration-300 hover:scale-110 hover:shadow-[0_6px_16px_rgba(0,0,0,0.1),inset_0_2px_5px_rgba(255,255,255,0.15),0_0_20px_rgba(255,0,128,0.3)] hover:border-pink-500/25 max-[360px]:w-12 max-[360px]:h-12 max-[360px]:text-2xl"
            style={{
              animation: isIntersecting ? 'subtle-pulse 3s infinite ease-in-out' : 'none',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
            }}
          >
            📧
          </div>
          <div className="flex flex-col gap-1 flex-1">
            <div className="text-[13px] text-tg-hint transition-colors duration-200 max-[360px]:text-xs">
              {t('PROFILE_SCREEN.FOOTER.EMAIL') || 'Email'}
            </div>
            <button 
              onClick={handleEmailClick}
              className="text-base font-semibold bg-transparent border-none p-0 text-left cursor-pointer transition-all duration-300 relative overflow-hidden max-[360px]:text-sm"
              style={{
                background: 'linear-gradient(135deg, #FF0080, #FF6B00)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              bigdeal.helpdesk@gmail.com
            </button>
          </div>
        </div>
        
        <div className="flex items-center gap-4 p-4 bg-tg-secondary-bg rounded-xl border border-pink-500/10 transition-all duration-300 relative overflow-hidden hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(0,0,0,0.12)] hover:border-pink-500/20 active:-translate-y-0.5 active:shadow-[0_4px_12px_rgba(0,0,0,0.1)] max-[360px]:p-3 max-[360px]:gap-2.5">
          <div 
            className="text-[32px] flex items-center justify-center w-16 h-16 bg-gradient-to-br from-pink-500/8 to-orange-500/8 rounded-full flex-shrink-0 border border-pink-500/15 shadow-[0_4px_12px_rgba(0,0,0,0.06),inset_0_2px_4px_rgba(255,255,255,0.1)] relative z-[2] transition-all duration-300 hover:scale-110 hover:shadow-[0_6px_16px_rgba(0,0,0,0.1),inset_0_2px_5px_rgba(255,255,255,0.15),0_0_20px_rgba(255,0,128,0.3)] hover:border-pink-500/25 max-[360px]:w-12 max-[360px]:h-12 max-[360px]:text-2xl"
            style={{
              animation: isIntersecting ? 'subtle-pulse 3s infinite ease-in-out' : 'none',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
            }}
          >
            📱
          </div>
          <div className="flex flex-col gap-1 flex-1">
            <div className="text-[13px] text-tg-hint transition-colors duration-200 max-[360px]:text-xs">
              {t('PROFILE_SCREEN.FOOTER.TELEGRAM') || 'Telegram'}
            </div>
            <button 
              onClick={handleTelegramClick}
              className="text-base font-semibold bg-transparent border-none p-0 text-left cursor-pointer transition-all duration-300 relative overflow-hidden active:opacity-80 active:scale-[0.98] max-[360px]:text-sm"
              style={{
                background: 'linear-gradient(135deg, #FF0080, #FF6B00)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              @BigDeal Support
            </button>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

